/**
 * PluginRenderer — Renders a plugin arche in a sandboxed iframe.
 *
 * Responsibilities:
 * - Fetches plugin config from the arche record
 * - Creates sandboxed iframe with strict CSP
 * - Establishes postMessage bridge via PluginBridgeHost
 * - Handles resize, navigation, and toast events
 * - Shows loading/error states
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { PluginBridgeHost } from './PluginBridge';
import { DARK_THEME, LIGHT_THEME, getThemeById } from '@fias/arche-sdk';

const API_BASE = import.meta.env.VITE_API_URL || '/v1';

/**
 * Returns true if the bundleRef points to an S3-hosted bundle that should
 * be served through the API proxy (because the S3 bucket blocks public access).
 */
export function isS3BundleRef(bundleRef: string): boolean {
  return bundleRef.startsWith('s3://') || bundleRef.startsWith('plugin-bundles/');
}

interface PluginRendererProps {
  archeId: string;
}

/** Matches PluginConfig from @fias/db-types (redefined for client use) */
interface PluginConfig {
  bundleRef: string;
  manifestVersion: string;
  permissions: Array<'user:profile:read' | 'entities:invoke' | 'storage:sandbox' | 'theme:read'>;
  sdkVersion: string;
  bundleSizeBytes: number;
  buildHash: string;
  themeId?: string;
  fontPairingId?: string;
  customTheme?: import('@fias/arche-sdk').FiasTheme;
}

/**
 * Returns true if the bundleRef should be fetched through the API proxy.
 * This includes:
 * - Source tarballs (local dev auto-approve, e.g. "user/plugin-sources/.../name.tar.gz")
 * - S3 keys (new format, e.g. "plugin-bundles/arc_xxx/v1/index.html")
 * - S3 URIs (old format, e.g. "s3://bucket/plugin-bundles/...")
 *
 * The S3 bucket blocks public access, so the browser cannot load S3 URLs
 * directly. All bundles must be proxied through the API endpoint.
 */
function shouldFetchViaApi(bundleRef: string): boolean {
  // Only external HTTPS URLs (non-S3) can be loaded directly by the browser
  if (bundleRef.startsWith('https://') && !bundleRef.includes('.s3.amazonaws.com/')) {
    return false;
  }
  return true;
}

export function PluginRenderer({ archeId }: PluginRendererProps) {
  const [pluginConfig, setPluginConfig] = useState<PluginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [srcdoc, setSrcdoc] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';

  // Fetch arche data to get plugin config
  useEffect(() => {
    let mounted = true;

    async function loadPlugin() {
      try {
        const result = (await api.getArche(archeId)) as Record<string, unknown>;
        const config = result.pluginConfig as PluginConfig | null;

        if (!config) {
          throw new Error('This arche does not have a valid plugin configuration');
        }

        // Fetch bundles through the API proxy (S3 bucket blocks public access)
        if (shouldFetchViaApi(config.bundleRef)) {
          const resp = await fetch(`${API_BASE}/arches/${archeId}/plugin-bundle`);
          if (!resp.ok) {
            const body = await resp.json().catch(() => null);
            const msg =
              (body as Record<string, Record<string, string>> | null)?.error?.message ||
              `Failed to load plugin bundle (${resp.status})`;
            throw new Error(msg);
          }
          const html = await resp.text();
          if (mounted) {
            setSrcdoc(html);
            setPluginConfig(config);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setPluginConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
          setLoading(false);
        }
      }
    }

    loadPlugin();
    return () => {
      mounted = false;
    };
  }, [archeId]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginConfig) return;

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      archId: archeId,
      permissions: pluginConfig.permissions,
      iframe: iframeRef.current,
      onResize: (height) => setIframeHeight(height),
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
      },
      onNavigate: (path) => {
        navigate(path);
      },
      getTheme: () => {
        if (pluginConfig.customTheme) return pluginConfig.customTheme;
        if (pluginConfig.themeId) {
          const entry = getThemeById(pluginConfig.themeId);
          if (entry) return entry.theme;
        }
        return themeMode === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      getUser: async () => ({
        userId: user?.userId ?? '',
        displayName: user?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      getAuthToken: async () => {
        const token = await authApi.getValidIdToken();
        return token ?? '';
      },
    });

    bridgeRef.current.init();
  }, [archeId, pluginConfig, navigate, user, themeMode]);

  // Send theme update when mode changes
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Clean up bridge on unmount
  useEffect(() => {
    return () => {
      bridgeRef.current?.destroy();
    };
  }, []);

  if (loading) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading plugin...</span>
        </div>
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Plugin Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pluginConfig) return null;

  const iframeProps = srcdoc ? { srcDoc: srcdoc } : { src: pluginConfig.bundleRef };

  return (
    <div className="w-full h-full min-h-0">
      <iframe
        ref={iframeRef}
        {...iframeProps}
        onLoad={handleIframeLoad}
        sandbox="allow-scripts allow-forms"
        referrerPolicy="no-referrer"
        className="w-full border-0 block"
        style={{
          height: iframeHeight != null ? `${iframeHeight}px` : '100%',
        }}
        title={`Plugin: ${archeId}`}
      />
    </div>
  );
}
